/**
 * `<EmptyState>` — THE canonical "no data" affordance (remaster U3 merged the
 * two prior implementations; frontend.md §4.2).
 *
 * Center-aligned: rounded surface icon, primary line, helper sub, optional
 * action row. Two APIs are supported so every historic call site keeps
 * compiling:
 *  - v2 style: `icon` + `title` + `detail` + `actions`
 *  - v1 style: `kind` + `title` + `detail` + `action`
 *
 * `kind` (frontend.md §4.2, D-006): a freshly-ingested corpus legitimately has
 * "extraction not done yet" states — `kind="indexing"` renders the title in the
 * warn tone so "indexing in progress (stage A: 1,240 / 4,000)" reads distinctly
 * from "no results".
 */

import type { ReactNode } from 'react';
import { Icon, type IconName } from '@/components/Icon';

interface EmptyStateProps {
  /** Optional icon. Default `'inbox'`. */
  icon?: IconName | ReactNode;
  /** `indexing` = extraction still running (warn tone); `empty` = genuinely no data. */
  kind?: 'empty' | 'indexing';
  /** Primary line — what's missing or what to do. */
  title: ReactNode;
  /** Helper sub-line — context or next step (for `indexing`, the progress string). */
  detail?: ReactNode;
  /** Optional action row (Button etc) shown below the sub. */
  actions?: ReactNode;
  /** v1-compat alias for `actions`. */
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon = 'inbox',
  kind = 'empty',
  title,
  detail,
  actions,
  action,
  className = '',
}: EmptyStateProps): JSX.Element {
  const iconEl = typeof icon === 'string'
    ? <Icon name={icon as IconName} size={20} />
    : icon;
  const actionRow = actions ?? action;
  return (
    <div
      className={`empty ${kind === 'indexing' ? 'empty--indexing' : ''} ${className}`.trim()}
      role="status"
    >
      <div className="empty-icon" aria-hidden>{iconEl}</div>
      <div className="empty-title">{title}</div>
      {detail && <div className="empty-sub">{detail}</div>}
      {actionRow}
    </div>
  );
}
